import {
  useEffect,
  useState,
  ChangeEvent,
  KeyboardEvent,
  FocusEvent,
  createRef,
  RefObject,
} from "react";
import Text from "../Text";

const KEY_CODE = {
  backspace: 8,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
};

export type VerificationCodeProps = {
  type?: "text" | "number";
  disabled?: boolean;
  fieldsCount: number;
  clear?: boolean;
  onChange?(value: string): void;
  onComplete?(value: string): void;
};

const VerificationCode = ({
  type = "number",
  disabled,
  fieldsCount,
  clear,
  onChange,
  onComplete,
}: VerificationCodeProps) => {
  const [values, setValues] = useState<string[]>(Array(fieldsCount).fill(""));
  const [inputRefs, setInputRefs] = useState<RefObject<HTMLInputElement>[]>([]);
  const [id] = useState(+new Date());

  useEffect(() => {
    setInputRefs((oldRefs) =>
      Array(fieldsCount)
        .fill(null)
        .map((_, index) => oldRefs[index] || createRef())
    );
  }, [fieldsCount]);

  useEffect(() => {
    inputRefs[0]?.current?.focus();
  }, [inputRefs]);

  useEffect(() => {
    if (clear) {
      setValues(Array(fieldsCount).fill(""));
    }
  }, [clear, fieldsCount]);

  const triggerChange = (newValues = values) => {
    const currentValue = newValues.join("");
    onChange?.(currentValue);

    if (currentValue.length === fieldsCount) {
      onComplete?.(currentValue);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (type === "number") {
      event.target.value = event.target.value.replace(/[^\d]/gi, "");
    }

    if (
      event.target.value === "" ||
      (type === "number" && !event.target.validity.valid)
    ) {
      return;
    }

    const index = parseInt(event.target.dataset.id as string);
    const { value } = event.target;
    const newValues = [...values];

    let next;
    if (value.length > 1) {
      let nextIndex = value.length + index - 1;
      if (nextIndex >= fieldsCount) {
        nextIndex = fieldsCount - 1;
      }
      next = inputRefs[nextIndex];
      const split = value.split("");

      split.forEach((item, i) => {
        const cursor = index + i;
        if (cursor < fieldsCount) {
          newValues[cursor] = item;
        }
      });
      setValues(newValues);
    } else {
      next = inputRefs[index + 1];
      newValues[index] = value;
      setValues(newValues);
    }

    if (next?.current) {
      next.current.focus();
      next.current.select();
    }

    triggerChange(newValues);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const index = parseInt(
      (event.target as HTMLInputElement).dataset.id as string
    );
    const prevIndex = index - 1;
    const nextIndex = index + 1;
    const prev = inputRefs[prevIndex];
    const next = inputRefs[nextIndex];

    switch (event.keyCode) {
      case KEY_CODE.backspace: {
        event.preventDefault();
        const vals = [...values];
        if (values[index]) {
          vals[index] = "";
          setValues(vals);
          triggerChange(vals);
        } else if (prev) {
          vals[prevIndex] = "";
          prev.current!.focus();

          setValues(vals);
          triggerChange(vals);
        }
        break;
      }
      case KEY_CODE.left:
        event.preventDefault();
        if (prev) {
          prev.current!.focus();
        }
        break;
      case KEY_CODE.right:
        event.preventDefault();
        if (next) {
          next.current!.focus();
        }
        break;
      case KEY_CODE.up:
      case KEY_CODE.down:
        event.preventDefault();
        break;
      default:
        break;
    }
  };

  const onFocus = (event: FocusEvent<HTMLInputElement>) =>
    event.target.select();

  return (
    <div>
      <Text kind="indigo" size="sm" className="mb-1">
        Código de segurança
      </Text>
      <div className="relative space-x-2">
        {values.map((value, index) => (
          <input
            className="h-10 w-10 rounded-lg border-2 border-tim-indigo bg-white text-center hover:border-tim-primary focus:border-tim-primary"
            key={String(`${id}-${index}`)}
            type={type === "number" ? "tel" : type}
            pattern={type === "number" ? "[0-9]*" : undefined}
            data-id={index}
            value={value}
            ref={inputRefs[index]}
            onChange={handleOnChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

export default VerificationCode;
